/* eslint-disable no-unused-vars */
import type { Dispatch, SetStateAction } from "react";
import type {
  FieldValues,
  FormState,
  UseFormClearErrors,
  UseFormHandleSubmit,
  UseFormResetField,
  UseFormSetValue,
} from "react-hook-form";
import type { QueryClient, UseMutationResult } from "react-query";
import type { ProblemDetails } from "../../../shared/types/problem-details.type";
import type { DateVal } from "../../../utils";
import type {
  DirectorAddress,
  ExportKeyPartiesType,
} from "./companyReport.type";

export type commonDateOfBirthType = Date | string | null;

export interface Profile {
  id: string;
  name: string | null;
  riskRating: string;
  status: string;
  type: string;
  internalId: string | null;
  customerId: string;
  companyId: string | null;
  assignedToId: number | null;
  assignedTo: string | null;
  safeNumber: string | null;
  organisationNumber: string | null;
  kycStatusUpdatedOn: string | null;
  createdAt: string;
  createdById: string;
  createdBy: string;
  modifiedAt: string;
  modifiedById: string;
  modifiedByUsername: string;
  kycApprovedAt: string;
  kycReviewOn: string | null;
  isSelected?: boolean;
  kycComments: string | null;
  details: {
    legalName: string | null;
  };
  keyPartyCount: number;
  noteCount: number;
  attachmentCount: number;
  isLocked: boolean;
  modeOfCreation?: string | null;
  formationDate?: string | null;
  internationalScore?: string | null;
  isKycMonitored?: boolean;
  openAlertCount?: number | null;
  source?: string | null;
}

export enum ProfileTypes {
  trust = "trust",
  individual = "individual",
  soleTrader = "soleTrader",
  company = "company",
  plc = "plc",
  partnership = "partnership",
  otherEntity = "otherEntity",
}

export enum ProfileCategory {
  individual = "individual",
  business = "business",
}

export interface GetProfilesResponse {
  profiles: Profile[];
  total: number;
  apiFailed: boolean;
}

export interface UpdateProfilePayload {
  id: string;
  name: string | null;
  internalId: string | null;
  riskRating: RiskRatings;
  status: KycProfileStatus;
  kycReviewOn: string | null;
  kycComments: string | null;
  assignedToId: number | null;
}

export interface GetAttachmentResponse {
  attachments: Attachments[];
  count: number;
  apiFailed: boolean;
}

export interface DownloadAttachment {
  downloadUrl: string;
  fileName: string;
}

export enum RiskRatings {
  notApplicable = "N/A",
  veryHigh = "Very High",
  high = "High",
  medium = "Medium",
  low = "Low",
  veryLow = "Very Low",
}

export enum KycProfileStatus {
  new = "New",
  approved = "Approved",
  declined = "Declined",
  pending = "Pending",
  cancelled = "Cancelled",
  referred = "Referred",
  closed = "Closed",
  approvedReviewDue = "Approved Review Due",
}

export interface UploadDocumentPayload {
  profileId: string;
  file: File;
  documentType: string;
  description?: string;
  contactName?: string;
  email?: string;
  website?: string;
  telephone?: string;
  turnover?: {
    currencyCode: string;
    amount: number | null;
  } | null;
  assetsUnderManagement?: {
    currencyCode: string;
    amount: number | null;
  } | null;
  dateOfBirth?: string | null;
  countryCode?: string | null;
  vatNo?: string | null;
  isListedOnExchange?: boolean | null;
  exchangeName?: string | null;
  organizationNumber?: string | null;
  internalContact?: string | null;
  internalEmail?: string | null;
  createdAt: string;
  createdById: number;
  createdBy: string;
  modifiedAt?: string | null;
  modifiedById?: number | null;
  modifiedBy?: string | null;
}

export interface Addresses {
  id: string;
  buildingDetails: string;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  countryCode: string;
  type: string;
  description: string;
  profileId: string;
}

export interface Address {
  category?: string;
  name: string;
  street1: string;
  street2: string;
  code: string;
}

export interface CurrencyCodeResponse {
  currencyCode: string[];
}

export interface Attachments {
  profileId: string;
  id: string;
  fileName: string;
  fileSizeInBytes: number;
  documentType: string;
  description?: string;
  createdAt: string;
  createdById: number;
  createdBy: string;
  modifiedAt: string;
  modifiedById: number;
  modifiedBy: string;
}

export interface AttachmentsUpdate {
  documentType: string;
  description?: string;
}

export interface Notes {
  profileId: string;
  description: string;
}

export interface NotesPayload {
  body: string;
}

export interface SaveNotesPayload {
  profileId: string;
  payload: NotesPayload;
}

export interface NotesResponse {
  id: string;
  body: string;
  isArchived: string;
  createdAt: string;
  createdById: number;
  createdBy: string;
  modifiedAt: string;
  modifiedById: number;
  modifiedBy: string;
}

export interface ViewNotesPayload {
  profileId: string;
  isArchived: boolean;
  SearchTerm: string;
  page: number;
  limit: number;
}

export interface ViewNotes {
  id: string;
  body: string;
  isArchived: boolean;
  createdAt: string;
  createdById: number;
  createdBy: string;
  modifiedAt: string;
  modifiedById: number;
  modifiedBy: string;
  count: number;
  linkedTo?: string;
}

export interface GetNotesResponse {
  notes: ViewNotes[];
  total: number;
  apiFailed: boolean;
}

export interface MockGetNotesResponse {
  items: ViewNotes[];
  count: number;
  totalSize?: number;
  apiFailed: boolean;
}

export interface Audits {
  payload?: { profileName: string };
  profiles?: string[];
  category: string[];
  subcategory: string[];
  action: string[];
  description: string[];
  users: string[];
  createdAt: string;
  createdBy: string[];
  createdById: string;
  datasets?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
}

export enum AuditsEnum {
  category = "category",
  subcategory = "subcategory",
  action = "action",
}

export interface GetAudits {
  count: number;
  data: Audits[];
  apiFailed: boolean;
}

export interface MockGetAudits {
  count: number;
  items: Audits[];
  apiFailed: boolean;
}

export interface KeyPartiesType {
  id: string;
  name?: string;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  keyPartyType: string;
  entityType: ProfileTypes;
  sharesPercentage?: number;
  description?: string;
  gender?: string | null;
  dateOfBirth?: commonDateOfBirthType;
  address?: AddressType;
  country?: string | null;
  activityCode?: string | null;
  organisationNumber?: string;
  safeNumber?: string | null;
  role?: string | null;
  createdAt: string;
  createdById: number;
  createdBy: string;
  modifiedAt: string;
  modifiedById: number;
  modifiedBy: string;
  percentageOfShares?: number;
  countryCode?: string | null;
  searchId?: string;
}

export interface KeyPartiesResponse {
  count: number;
  totalSize?: number;
  items: KeyPartiesType[];
  apiFailed: boolean;
}

export interface KeyPartiesFormValues {
  name: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  sharesPercentage?: number;
  gender?: string | null;
  dateOfBirth?: commonDateOfBirthType;
  country?: string | null;
  activityCode?: string | null;
  organisationNumber?: string;
  safeNumber?: string | null;
  role?: string | null;
  buildingDetails: string;
  city: string;
  street: string;
  postalCode: string;
  region: string;
  description: string;
}

export interface AddressType {
  description?: string | null;
  buildingDetails?: string;
  street?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  countryCode?: string | null;
  type?: string;
}

export interface NewKeyPartyPayload {
  name?: string;
  firstName?: string | null;
  lastName?: string | null;
  middleName?: string | null;
  keyPartyType?: string;
  entityType: ProfileTypes;
  gender?: string | null;
  dateOfBirth?: commonDateOfBirthType;
  countryCode?: string | null;
  organisationNumber?: string;
  activityCode?: string;
  percentageOfShares?: number | null;
  role?: string;
  address?: AddressType;
  description?: string | null;
}

export interface NewKeyPartyPayloadItems {
  items: NewKeyPartyPayload[];
}

export interface AddNewKeyPartyMutation {
  profileId: string;
  keyPartyPayload: NewKeyPartyPayloadItems;
}

export interface UpdateKeyPartyMutation {
  profileId: string;
  keyPartyPayload: EditKeyPartiesPayload;
  keyPartyId: string;
}

export interface EditKeyPartiesPayload {
  name?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  gender?: string | null;
  dateOfBirth?: commonDateOfBirthType;
  countryCode?: string | null;
  organisationNumber?: string;
  safeNumber?: string;
  activityCode?: string;
  percentageOfShares?: number | null;
  role?: string;
  address?: AddressType;
  description?: string;
}

export interface UboDetails {
  id: string;
  name?: string;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  keyPartyType: string;
  entityType: ProfileTypes;
  gender?: string | null;
  dateOfBirth?: commonDateOfBirthType;
  country?: string | null;
  organisationNumber?: string;
  activityCode?: string | null;
  sharesPercentage?: number;
  role?: string | null;
  address?: AddressType;
  createdBy: string;
  createdAt: string;
  createdById: number;
  modifiedAt: string;
  modifiedById: number;
  modifiedBy: string;
  countryCode?: string | null;
}

export interface UboDetailsResponse {
  items: UboDetails[];
  count: number;
}

export enum AddressTypes {
  registered = "registered",
  trading = "trading",
  other = "other",
}

export interface CreateNewKeyPartyValues extends FieldValues {
  profileType: string;
  name: string;
  gender?: string;
  dateOfBirth: DateVal;
  keyPartyType: string;
  role: string;
  organisationNumber: string;
  safeNumber?: string;
  activityCode: string;
  sharePercentage?: number | null;
  address: AddressType;
  country: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  description?: string | null;
}

export interface UboFormValues {
  profileType: string;
  name: string;
  organisationNumber: string;
  activityCode: string;
  gender: string | null;
  dateOfBirth: DateVal;
  address: AddressType;
  country: string | null;
}

export interface GetKeyPartiesQueryParams {
  page: number;
  limit?: number;
  pageSize?: number;
  entityType?: string;
  keyPartyTypes?: string[];
}

export type EditModeType = boolean | null | KeyPartiesType | UboDetails;

export interface CreateProfileExport {
  exportPayload: ExportProfilePayload;
  queryParams: { language: string };
}

export interface ExportProfilePayload {
  name: string;
  type: string;
  formationDate: Date | string;
  companyId: string;
  internalId: string | null;
  companyReportFilename?: string;
  companyReportFolderName?: string;
  addresses: DirectorAddress[];
  profileDetails: {
    organisationNumber?: string;
    legalName: string | null;
    tradingName: string | null;
    aliases?: string[];
    activity: string;
    description: string;
    website: string;
    telephone: string;
    turnover?: {
      currencyCode: string;
      amount: number;
    };
    assetsUnderManagement?: {
      currencyCode: string;
      amount: number;
    };
    vatNo: string;
    email: string;
    countryCode?: string;
    isListedOnExchange?: true;
    exchangeName?: string;
    internationalScore?: string;
  };
  keyParties: ExportKeyPartiesType[];
  source?: string;
  fileName?: string;
}

export interface ExportProfileResponse {
  id: string;
  profileUrl: string;
}

export enum ModeOfCreation {
  import = "import",
  manual = "manual",
  batchUpload = "batchUpload",
}

export enum ProfileFields {
  status = "status",
  riskRating = "riskRating",
  id = "id",
  name = "name",
  internalId = "internalId",
  kycReviewOn = "kycReviewOn",
  kycComments = "kycComments",
  assignedToId = "assignedToId",
  root = "root",
}

export interface UpdatedPayloadProps {
  kycComments: string | null;
  assignedToId: number | null;
  status: KycProfileStatus;
  id?: string;
  name?: string | null;
  internalId?: string | null;
  riskRating?: RiskRatings;
  kycReviewOn?: string | null;
}

export interface HandleEditButtonClickProps {
  editItem: string;
  isEditComments: boolean;
  setIsProfileNameEditing: Dispatch<SetStateAction<boolean>>;
  setIsInternalIdEditing: Dispatch<SetStateAction<boolean>>;
  resetField: UseFormResetField<UpdateProfilePayload>;
  setIsCommentEdit: Dispatch<SetStateAction<boolean>>;
  setIsAssignedToPopUp: Dispatch<SetStateAction<boolean>>;
  setIsWarningPopUp: Dispatch<SetStateAction<boolean>>;
  isProfileNameEditing: boolean;
  isInternalIdEditing: boolean;
  checkIsDirtyFields: (_field: string) => boolean;
  isAssignedToPopUp: boolean;
  setValue: UseFormSetValue<UpdateProfilePayload>;
  profileHeaderDetails: Profile | undefined;
  clearErrors: UseFormClearErrors<UpdateProfilePayload>;
}

export interface HandleCancelClickProps {
  editItem: string;
  setValue: UseFormSetValue<UpdateProfilePayload>;
  profileHeaderDetails: Profile | undefined;
  setIsProfileNameEditing: Dispatch<SetStateAction<boolean>>;
  isProfileNameEditing: boolean;
  clearErrors: UseFormClearErrors<UpdateProfilePayload>;
  setIsInternalIdEditing: Dispatch<SetStateAction<boolean>>;
  isInternalIdEditing: boolean;
  setIsCommentEditing: Dispatch<SetStateAction<boolean>>;
  isCommentEditing: boolean;
}

export interface HandleProfileUpdateFailuresProps {
  updatingFieldName?: ProfileFields | string;
  error: ProblemDetails;
  setIsProfileNameEditing: Dispatch<SetStateAction<boolean>>;
  setIsInternalIdEditing: Dispatch<SetStateAction<boolean>>;
  displayErrorMessage: (
    payload: UpdateProfilePayload,
    message: string,
    fieldValue: ProfileFields
  ) => void;
  displayErrorSnackbar: () => void;
  payload: UpdateProfilePayload;
  showSnackbar: (message: string, isError?: boolean) => void;
  translate: (key: string) => string;
}

export interface HandleProfileUpdateSuccessProps {
  updatingFieldName?: ProfileFields | string;
  setIsProfileNameEditing: Dispatch<SetStateAction<boolean>>;
  setIsInternalIdEditing: Dispatch<SetStateAction<boolean>>;
  setIsAssignedToPopUp: Dispatch<SetStateAction<boolean>>;
  showSnackbar: (message: string, isError?: boolean) => void;
  translate: (key: string) => string;
}

export interface HandleReviewDateChangeProps {
  reviewedOnDate: DateVal;
  setIsValidReviewedDate: Dispatch<SetStateAction<boolean>>;
  profilePayload: UpdateProfilePayload | undefined;
  updateProfile: (payload: UpdateProfilePayload) => Promise<void>;
}

export interface HandleRemoveProfileFromMonitoringProps {
  setIsMonitoringPopupOpen: Dispatch<SetStateAction<boolean>>;
  isMonitoringPopupOpen: boolean;
  setIsUpdateApiCalled: Dispatch<SetStateAction<boolean>>;
  removeProfileFromMonitoring: UseMutationResult<
    unknown,
    unknown,
    {
      profileIds: string[];
    },
    unknown
  >;
  profileId: string;
  setToggleMonitoring: Dispatch<SetStateAction<boolean>>;
  toggleMonitoring: boolean;
  displayErrorSnackbar: () => void;
  queryClient: QueryClient;
}

export interface CreateProfilePdfReportPayload {
  type: string;
  source: string;
  criteria: {
    profileId: string;
  };
}

export interface ProfileUpdateProps {
  submittingItem: string;
  handleSubmit: UseFormHandleSubmit<UpdateProfilePayload, undefined>;
  formState: FormState<UpdateProfilePayload>;
  updateProfile: (
    payload: UpdateProfilePayload,
    updatingFieldName?: string
  ) => void;
  setIsAssignedToPopUp: Dispatch<SetStateAction<boolean>>;
  profilePayload: UpdateProfilePayload | undefined;
  profileData: Profile | undefined;
  clearErrors: UseFormClearErrors<UpdateProfilePayload>;
}

export interface AddToMonitoringProps {
  setIsUpdateApiCalled: Dispatch<SetStateAction<boolean>>;
  profileId: string;
  setToggleMonitoring: Dispatch<SetStateAction<boolean>>;
  toggleMonitoring: boolean;
  displayErrorSnackbar: () => void;
  setNoCreditsAvailableError: Dispatch<SetStateAction<boolean>>;
  queryClient: QueryClient;
  addToKycMonitoringMutation: UseMutationResult<
    boolean,
    unknown,
    {
      profileIds: string[];
    },
    unknown
  >;
}
